import { useState } from "react";

export type DialogSteps = {
  step: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  back: () => void;
  goTo: (step: number) => void;
};

const clamp = (step: number, total: number) => Math.min(Math.max(step, 1), total);

/**
 * Step state for a wizard dialog, 1-based. Holds no data and no context: a wizard that skips a step
 * or has to do work before moving (abort a stream, reset a field) does it at the call site and then
 * calls `goTo`.
 */
export const useDialogSteps = ({
  total,
  initialStep = 1,
}: {
  total: number;
  initialStep?: number;
}): DialogSteps => {
  const [step, setStep] = useState(() => clamp(initialStep, total));

  return {
    step,
    total,
    isFirst: step === 1,
    isLast: step === total,
    next: () => setStep((current) => clamp(current + 1, total)),
    back: () => setStep((current) => clamp(current - 1, total)),
    goTo: (target) => setStep(clamp(target, total)),
  };
};
